import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { usePaneMode } from './PaneContext';
import { Sheet } from './Sheet';
import { WS_COLOR, WS_FONT, WS_RADIUS } from './tokens';

export interface FactItem {
  label: string;
  value: ReactNode;
}

/** Label / value pairs. Values are right-aligned with tabular figures. */
export function FactList({ items }: { items: readonly FactItem[] }) {
  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        columnGap: 4,
        rowGap: 1.5,
        m: 0,
        fontSize: 14,
        lineHeight: '20px',
        '& dt': { color: WS_COLOR.textMuted },
        '& dd': { m: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums lining-nums' },
      }}
    >
      {items.map((item) => (
        <Box key={item.label} sx={{ display: 'contents' }}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </Box>
      ))}
    </Box>
  );
}

export interface DetailPaneProps {
  title: string;
  /** Record identifier in mono above the title. */
  id?: string;
  /** Status chip(s) at the top right. */
  status?: ReactNode;
  children: ReactNode;
  /** Pinned to the bottom of the pane. */
  actions?: ReactNode;
  /** Names the region for assistive technology. Defaults to the title. */
  label?: string;
}

/**
 * The record pane beside a registry (DESIGN.md layout 2). It scrolls on its own and keeps
 * its actions pinned at the bottom. It is one flat surface: sections inside it are separated
 * by space and rules, never by nested cards.
 *
 * When the enclosing layout is too narrow for a pane, this same component renders as a
 * Sheet, so a module writes the record once.
 */
export function DetailPane({ title, id, status, children, actions, label }: DetailPaneProps) {
  const mode = usePaneMode();
  if (mode.narrow) {
    // No room beside the table: the same record, in a Sheet that owns the screen.
    return (
      <Sheet open={mode.open} title={title} onClose={mode.onClose} actions={actions}>
        {id || status ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            {id ? (
              <Typography
                component="div"
                sx={{ fontFamily: WS_FONT.mono, fontSize: 12, color: WS_COLOR.textMuted }}
              >
                {id}
              </Typography>
            ) : null}
            {status}
          </Box>
        ) : null}
        {children}
      </Sheet>
    );
  }
  return (
    <Box
      component="aside"
      aria-label={label ?? title}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        backgroundColor: WS_COLOR.surface,
        border: `1px solid ${WS_COLOR.border}`,
        borderRadius: `${WS_RADIUS.card}px`,
      }}
    >
      <Box
        // Focusable so a keyboard user can scroll it (a scroll region with nothing focusable
        // inside is unreachable otherwise).
        tabIndex={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {id ? (
              <Typography
                component="div"
                sx={{
                  fontFamily: WS_FONT.mono,
                  fontSize: 12,
                  lineHeight: '16px',
                  color: WS_COLOR.textMuted,
                }}
              >
                {id}
              </Typography>
            ) : null}
            <Typography variant="h2" component="h2" sx={{ overflowWrap: 'anywhere' }}>
              {title}
            </Typography>
          </Box>
          {status ? <Box sx={{ flex: 'none' }}>{status}</Box> : null}
        </Box>
        {children}
      </Box>
      {actions ? (
        <Box
          sx={{
            flex: 'none',
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            p: 4,
            borderTop: `1px solid ${WS_COLOR.border}`,
          }}
        >
          {actions}
        </Box>
      ) : null}
    </Box>
  );
}
